import React, { useRef, useState, useEffect } from 'react'
import { Phone, Play, Square, CheckCircle2, XCircle } from 'lucide-react'
import { voiceClient } from '@/lib/api/voice.client'

const BAR_COUNT = 14

interface InlineAudioPlayerProps {
  recordingUrl: string
  transferNumber: string
  extension?: string
  resultLabel: string
  successful: boolean
  showTime?: boolean
}

export const InlineAudioPlayer: React.FC<InlineAudioPlayerProps> = ({
  recordingUrl,
  transferNumber,
  extension,
  resultLabel,
  successful,
  showTime = false,
}) => {
  const audioRef = useRef<HTMLVideoElement>(null)
  const animFrameRef = useRef<number | undefined>(undefined)
  const restFrameRef = useRef<number | undefined>(undefined)
  const [isFlipped, setIsFlipped] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [bars, setBars] = useState<number[]>(Array(BAR_COUNT).fill(20))

  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        setBars(prev =>
          prev.map((h, i) => {
            const t = Date.now() * 0.002
            const pos = (i / BAR_COUNT) * Math.PI * 2
            const target = Math.max(
              10,
              Math.sin(pos + t * 2.0) * 25 +
                35 +
                Math.sin(-pos + t * 1.3) * 15 +
                Math.sin(pos * 0.5 + t * 0.8) * 10,
            )
            return h + (target - h) * 0.15
          }),
        )
        animFrameRef.current = requestAnimationFrame(animate)
      }
      animate()
    } else {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
      const toRest = () => {
        setBars(prev => {
          const next = prev.map(h => h + (20 - h) * 0.1)
          if (!next.every(h => Math.abs(h - 20) < 1))
            restFrameRef.current = requestAnimationFrame(toRest)
          return next
        })
      }
      toRest()
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
      if (restFrameRef.current) cancelAnimationFrame(restFrameRef.current)
    }
  }, [isPlaying])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTime = () => setCurrentTime(audio.currentTime)
    const onMeta = () => setDuration(audio.duration)
    const onEnded = () => {
      setIsPlaying(false)
      setIsFlipped(false)
      setCurrentTime(0)
    }
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('ended', onEnded)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('ended', onEnded)
    }
  }, [])

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${Math.floor(s % 60)
      .toString()
      .padStart(2, '0')}`

  const handlePlay = async () => {
    const audio = audioRef.current
    if (!audio) return
    setIsFlipped(true)
    if (!audioUrl) {
      setIsFetching(true)
      try {
        const { signedUrl } = await voiceClient.getSignedUrl(recordingUrl)
        setAudioUrl(signedUrl)
        audio.src = signedUrl
        audio.load()
        await audio.play()
        setIsPlaying(true)
      } catch {
        setIsFlipped(false)
      } finally {
        setIsFetching(false)
      }
      return
    }
    await audio.play()
    setIsPlaying(true)
  }

  const handleStop = () => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
    setIsPlaying(false)
    setIsFlipped(false)
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || duration === 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div style={{ perspective: '600px' }} className="w-full min-w-0">
      <video ref={audioRef} style={{ display: 'none' }} />
      <div
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.3s ease',
          transform: isFlipped ? 'rotateX(180deg)' : 'rotateX(0deg)',
          position: 'relative',
          height: '20px',
        }}
      >
        {/* Front face — normal phone row */}
        <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
          className="absolute inset-0 flex items-center gap-2 text-muted-foreground"
        >
          <button
            onClick={handlePlay}
            className="flex-shrink-0 w-3 h-3 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="Play recording"
          >
            <Play className="w-3 h-3 fill-current" />
          </button>
          <Phone className="w-3 h-3 flex-shrink-0" />
          <span className="text-xs truncate min-w-0">
            {transferNumber}
            {extension && ` x ${extension}`}
          </span>
          <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-muted-foreground">{resultLabel}</span>
            {successful ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-success-dim" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Back face — mini visualizer (light variant) */}
        <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateX(180deg)',
          }}
          className="absolute inset-0 flex items-center gap-2"
        >
          <button
            onClick={handleStop}
            className="flex-shrink-0 w-3 h-3 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title="Stop"
          >
            <Square className="w-3 h-3 fill-current" />
          </button>
          <div
            onClick={handleSeek}
            className="flex-1 h-4 bg-muted rounded overflow-hidden relative flex items-end gap-px px-0.5 min-w-0 cursor-pointer"
          >
            {bars.map((h, i) => (
              <div
                key={i}
                className="bg-muted-foreground/50 flex-1"
                style={{ height: `${h}%`, minHeight: '2px' }}
              />
            ))}
            <div
              className="absolute top-0 left-0 h-full bg-muted-foreground/20 transition-all duration-75"
              style={{ width: `${progress}%` }}
            />
          </div>
          {showTime && (
            <span className="text-xs text-gray-500 dark:text-muted-foreground font-mono flex-shrink-0 tabular-nums">
              {isFetching
                ? '…'
                : `${formatTime(currentTime)} / ${formatTime(duration)}`}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
